import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Draggable from "react-draggable";
import ReactTooltip from "react-tooltip";
import Portal from "../../../common/components/Portal";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";
import ChartItem from "../ChartItem";
import SmartChartDataEditForm from "../SmartChartDataEditForm";
import {
    bandChildrenAsArray,
    hierarchyBandEntriesToRecord,
    patchHierarchyNodeById,
    withInferredHierarchyBandType
} from "../../utils/executiveSummarySelectionUtils";
import "../../smartCharts.css";
import "../../executiveSummaryHierarchyTile.css";

const noopInnerRef = () => {};

function hierarchyBandKey(node) {
    return node?.band_id ?? "";
}

const dndProvidedStatic = {
    innerRef: noopInnerRef,
    draggableProps: {},
    dragHandleProps: {},
    placeholder: null
};

const GROUPED_EXEC_SUMMARY_CONFIG_DEFAULTS = {
    group_by: "building",
    sort_by: "building_name",
    sort_direction: "asc"
};

const HEADING_PREFIX_OPTIONS = [
    { value: "building_name", label: "Building Name" },
    { value: "building_description", label: "Building Description" }
];

const GROUPED_EXEC_SUMMARY_CONFIG_OPTIONS = {
    group_by: [
        { value: "building", label: "Building" },
        { value: "band", label: "Band" }
    ],
    sort_by: [
        { value: "building_name", label: "Building Name" },
        { value: "building_description", label: "Building Description" }
    ],
    sort_direction: [
        { value: "asc", label: "Ascending" },
        { value: "desc", label: "Descending" }
    ]
};

function groupedExecutiveSummaryConfigValue(tile, key) {
    const value = tile?.[key];
    return typeof value === "string" && value ? value : GROUPED_EXEC_SUMMARY_CONFIG_DEFAULTS[key];
}

function hierarchyLeafToChartItem(node, chartKey) {
    if (node.band_type === "image") {
        return {
            name: node.name || "Image Band",
            isSelected: true,
            defaultValue: "",
            totalChartCount: Array.isArray(node.data) ? node.data.length : 0,
            isImageBand: true,
            isMemo: false,
            docKey: chartKey,
            hasConfig: true,
            config: null,
            entityIcon: "smart-chart-image-item.svg",
            isUserDoc: false,
            isImage: false,
            isCAband: false,
            isMultipleRecommendation: false,
            isRecommendation: false,
            isOnePager: false,
            isFilterReport: false,
            isIndividualBuilding: false,
            isBbi: false,
            isBai: false,
            isBsi: false,
            isExecutiveSummaryParent: false,
            isAssetOnePager: false,
            isTOC: false,
            isSortedRecom: false,
            isDynamicSystem: false,
            isDynamicMasterFilter: false,
            isDynamicBBI: false,
            isDynamicBSI: false,
            isDynamicBAI: false,
            hasPageBreak: true,
            tileData: { ...node },
            flatBandData: null,
            innerHierarchyNodeId: hierarchyBandKey(node) || null
        };
    }
    if (node.band_type === "text") {
        return {
            name: node.name || "Text Band",
            isSelected: true,
            defaultValue: "",
            totalChartCount: 0,
            isImageBand: false,
            isMemo: true,
            docKey: chartKey,
            hasConfig: false,
            config: null,
            entityIcon: "clipboard-text.svg",
            isUserDoc: false,
            isImage: false,
            isCAband: false,
            isMultipleRecommendation: false,
            isRecommendation: false,
            isOnePager: false,
            isFilterReport: false,
            isIndividualBuilding: false,
            isBbi: false,
            isBai: false,
            isBsi: false,
            isExecutiveSummaryParent: false,
            isAssetOnePager: false,
            isTOC: false,
            isSortedRecom: false,
            isDynamicSystem: false,
            isDynamicMasterFilter: false,
            isDynamicBBI: false,
            isDynamicBSI: false,
            isDynamicBAI: false,
            hasPageBreak: false,
            tileData: { ...node },
            flatBandData: null,
            innerHierarchyNodeId: hierarchyBandKey(node) || null
        };
    }
    return null;
}

function hierarchyGroupToChartItem(node, chartKey) {
    const children = bandChildrenAsArray(node?.band);
    return {
        name: node?.name || "Group",
        isSelected: true,
        defaultValue: "",
        totalChartCount: children.length,
        isImageBand: false,
        isMemo: false,
        docKey: chartKey,
        hasConfig: true,
        config: null,
        entityIcon: "smart-chart-exec-sum-folder.svg",
        isUserDoc: false,
        isImage: false,
        isCAband: false,
        isMultipleRecommendation: false,
        isRecommendation: false,
        isOnePager: false,
        isFilterReport: false,
        isIndividualBuilding: false,
        isBbi: false,
        isBai: false,
        isBsi: false,
        isExecutiveSummaryParent: true,
        isAssetOnePager: false,
        isTOC: false,
        isSortedRecom: false,
        isDynamicSystem: false,
        isDynamicMasterFilter: false,
        isDynamicBBI: false,
        isDynamicBSI: false,
        isDynamicBAI: false,
        hasPageBreak: false,
        tileData: { ...node, exec_sum_parent_wrapper: true },
        flatBandData: null,
        innerHierarchyNodeId: hierarchyBandKey(node) || null
    };
}

const hierarchyChartItemSharedProps = ({
    chartKey,
    chartItemTooltipId,
    entity,
    bandName,
    entireChartData,
    isPreviewDocLoading,
    selectedDocPreview,
    handleShowDocPreview,
    updateDocHeaderConfig,
    updatePageBreakConfig,
    toggleImageListForBandModal,
    toggleImageBandDeleteConfirmationModal,
    editImageBand,
    openChartItemConfigModal,
    openExecutiveSummaryHierarchyConfig,
    onOpenNestedExecutiveSummaryGroup,
    setSmartChartData,
    currentChartData,
    hierarchyModalReadOnly,
    hideMemoImageEditControls
}) => ({
    chartKey,
    chartItemTooltipId,
    entity,
    bandName,
    entireChartData,
    isPreviewDocLoading,
    selectedDocPreview,
    handleShowDocPreview,
    updateDocHeaderConfig,
    updatePageBreakConfig,
    toggleImageListForBandModal,
    toggleImageBandDeleteConfirmationModal,
    editImageBand,
    openChartItemConfigModal,
    openExecutiveSummaryHierarchyConfig,
    onOpenNestedExecutiveSummaryGroup,
    setSmartChartData,
    currentChartData,
    hierarchyModalReadOnly,
    hideMemoImageEditControls
});

function HierarchyLeafChartItem({
    node,
    chartKey,
    chartItemTooltipId,
    entity,
    bandName,
    entireChartData,
    isPreviewDocLoading,
    selectedDocPreview,
    handleShowDocPreview,
    updateDocHeaderConfig,
    updatePageBreakConfig,
    toggleImageListForBandModal,
    toggleImageBandDeleteConfirmationModal,
    editImageBand,
    openChartItemConfigModal,
    openExecutiveSummaryHierarchyConfig,
    onOpenNestedExecutiveSummaryGroup,
    setSmartChartData,
    currentChartData,
    hierarchyModalReadOnly = false,
    hideMemoImageEditControls = false
}) {
    const item = hierarchyLeafToChartItem(node, chartKey);

    if (!item) return null;
    return (
        <ChartItem
            item={item}
            embedInHierarchyConfigModal
            hierarchyModalReadOnly={hierarchyModalReadOnly}
            chartItemTooltipId={chartItemTooltipId}
            provided={dndProvidedStatic}
            index={0}
            entity={entity}
            currentBand={bandName}
            setSmartChartData={setSmartChartData}
            chartItemData={null}
            openChartItemConfigModal={openChartItemConfigModal}
            openExecutiveSummaryHierarchyConfig={openExecutiveSummaryHierarchyConfig}
            onOpenNestedExecutiveSummaryGroup={onOpenNestedExecutiveSummaryGroup}
            toggleImageListForBandModal={toggleImageListForBandModal}
            toggleImageBandDeleteConfirmationModal={toggleImageBandDeleteConfirmationModal}
            editImageBand={editImageBand}
            isEdit={false}
            updatePageBreakConfig={updatePageBreakConfig}
            isExcelExport={false}
            isSkeletonView={false}
            buildingMfilter={[]}
            additionMfilter={[]}
            siteMfilter={[]}
            currentChartData={currentChartData}
            handleShowDocPreview={handleShowDocPreview}
            isPreviewDocLoading={isPreviewDocLoading}
            selectedDocPreview={selectedDocPreview}
            updateDocHeaderConfig={updateDocHeaderConfig}
            entireChartData={entireChartData}
            hideMemoImageEditControls={hideMemoImageEditControls}
        />
    );
}

function HierarchyGroupChartItem({
    node,
    chartKey,
    chartItemTooltipId,
    entity,
    bandName,
    entireChartData,
    isPreviewDocLoading,
    selectedDocPreview,
    handleShowDocPreview,
    updateDocHeaderConfig,
    updatePageBreakConfig,
    toggleImageListForBandModal,
    toggleImageBandDeleteConfirmationModal,
    editImageBand,
    openChartItemConfigModal,
    openExecutiveSummaryHierarchyConfig,
    onOpenNestedExecutiveSummaryGroup,
    setSmartChartData,
    currentChartData,
    hierarchyModalReadOnly = false,
    hideMemoImageEditControls = false
}) {
    const item = hierarchyGroupToChartItem(node, chartKey);
    return (
        <ChartItem
            item={item}
            embedInHierarchyConfigModal
            hierarchyModalReadOnly={hierarchyModalReadOnly}
            chartItemTooltipId={chartItemTooltipId}
            provided={dndProvidedStatic}
            index={0}
            entity={entity}
            currentBand={bandName}
            setSmartChartData={setSmartChartData}
            chartItemData={null}
            openChartItemConfigModal={openChartItemConfigModal}
            openExecutiveSummaryHierarchyConfig={openExecutiveSummaryHierarchyConfig}
            onOpenNestedExecutiveSummaryGroup={onOpenNestedExecutiveSummaryGroup}
            toggleImageListForBandModal={toggleImageListForBandModal}
            toggleImageBandDeleteConfirmationModal={toggleImageBandDeleteConfirmationModal}
            editImageBand={editImageBand}
            isEdit={false}
            updatePageBreakConfig={updatePageBreakConfig}
            isExcelExport={false}
            isSkeletonView={false}
            buildingMfilter={[]}
            additionMfilter={[]}
            siteMfilter={[]}
            currentChartData={currentChartData}
            handleShowDocPreview={handleShowDocPreview}
            isPreviewDocLoading={isPreviewDocLoading}
            selectedDocPreview={selectedDocPreview}
            updateDocHeaderConfig={updateDocHeaderConfig}
            entireChartData={entireChartData}
            hideMemoImageEditControls={hideMemoImageEditControls}
        />
    );
}

function NestedGroupGrid({
    nodes,
    parentNodeId = null,
    chartKey,
    entity,
    bandName,
    entireChartData,
    isPreviewDocLoading,
    selectedDocPreview,
    handleShowDocPreview,
    updateDocHeaderConfig,
    updatePageBreakConfig,
    toggleImageListForBandModal,
    toggleImageBandDeleteConfirmationModal,
    editImageBand,
    openChartItemConfigModal,
    openExecutiveSummaryHierarchyConfig,
    onOpenNestedExecutiveSummaryGroup,
    setSmartChartData,
    currentChartData,
    onReorderGroupChildren,
    hierarchyModalReadOnly = false,
    hierarchyModalGroupedBand = false,
    hideMemoImageEditControls = false
}) {
    const ordered = useMemo(() => (nodes || []).map(withInferredHierarchyBandType), [nodes]);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const sortableIds = useMemo(() => ordered.map(n => hierarchyBandKey(n)).filter(Boolean), [ordered]);
    const sharedProps = hierarchyChartItemSharedProps({
        chartKey,
        entity,
        bandName,
        entireChartData,
        isPreviewDocLoading,
        selectedDocPreview,
        handleShowDocPreview,
        updateDocHeaderConfig,
        updatePageBreakConfig,
        toggleImageListForBandModal,
        toggleImageBandDeleteConfirmationModal,
        editImageBand,
        openChartItemConfigModal,
        openExecutiveSummaryHierarchyConfig,
        onOpenNestedExecutiveSummaryGroup,
        setSmartChartData,
        currentChartData,
        hierarchyModalReadOnly,
        hideMemoImageEditControls
    });

    const onDragEnd = useCallback(
        event => {
            const { active, over } = event;
            if (!over || active.id === over.id || hierarchyModalReadOnly || !ordered?.length) return;
            const oldIndex = ordered.findIndex(c => hierarchyBandKey(c) === String(active.id));
            const newIndex = ordered.findIndex(c => hierarchyBandKey(c) === String(over.id));
            if (oldIndex < 0 || newIndex < 0) return;
            onReorderGroupChildren?.(parentNodeId, arrayMove(ordered, oldIndex, newIndex));
        },
        [ordered, onReorderGroupChildren, parentNodeId, hierarchyModalReadOnly]
    );

    if (!ordered?.length) return null;

    const gridContent = ordered.map(n => {
        if (n.band_type === "group" || n.is_grouped_band === true) {
            const chartItemTooltipId = `hier-cfg-${String(chartKey).replace(/[^a-zA-Z0-9_-]/g, "_")}-${hierarchyBandKey(n)}`;
            return (
                <SortableTopLevelCard key={hierarchyBandKey(n)} id={hierarchyBandKey(n)}>
                    <div className="es-hier-cfg-tile-wrap-inner es-hier-cfg-tile-wrap-inner--solo es-hier-cfg-group-tile-only">
                        <HierarchyGroupChartItem node={n} chartItemTooltipId={chartItemTooltipId} {...sharedProps} />
                    </div>
                </SortableTopLevelCard>
            );
        }
        const chartItemTooltipId = `hier-cfg-${String(chartKey).replace(/[^a-zA-Z0-9_-]/g, "_")}-${hierarchyBandKey(n)}`;
        return (
            <SortableTopLevelCard key={hierarchyBandKey(n)} id={hierarchyBandKey(n)}>
                <div className="es-hier-cfg-tile-wrap-inner es-hier-cfg-tile-wrap-inner--solo">
                    <HierarchyLeafChartItem node={n} chartItemTooltipId={chartItemTooltipId} {...sharedProps} />
                </div>
            </SortableTopLevelCard>
        );
    });

    const gridClassName = parentNodeId == null ? "es-hier-cfg-top-grid" : "es-hier-cfg-leaf-grid";
    const wrapClassName = parentNodeId == null ? "mt-0" : "mt-1";
    const groupedBandClassName = currentChartData?.type?.[chartKey]?.is_grouped_band ? "es-hier-cfg-grouped-band" : "";

    if (hierarchyModalReadOnly) {
        return (
            <div className={`drag-main es-hier-cfg-drag-main ${wrapClassName} ${groupedBandClassName}`}>
                <div className={`${gridClassName} dragble-area es-hier-cfg-readonly-top-grid`}>{gridContent}</div>
            </div>
        );
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} autoScroll={{ threshold: { x: 0, y: 0.2 } }}>
            <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                <div className={`drag-main es-hier-cfg-drag-main ${wrapClassName} ${groupedBandClassName}`}>
                    <div className={`${gridClassName} dragble-area`}>{gridContent}</div>
                </div>
            </SortableContext>
        </DndContext>
    );
}

function SortableTopLevelCard({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined
    };
    return (
        <div ref={setNodeRef} style={style} className={`es-hier-cfg-sort-cell ${isDragging ? "is-dragging" : ""}`}>
            <div className="es-hier-cfg-sort-inner" {...attributes} {...listeners}>
                {children}
            </div>
        </div>
    );
}

/** Top-level hierarchy layout without drag-and-drop (template view / read-only). */
function HierarchyTopLevelGridReadOnly({ band: topNodes, chartKey, onReorderGroupChildren, onOpenNestedExecutiveSummaryGroup, ...sharedProps }) {
    return (
        <NestedGroupGrid
            nodes={topNodes}
            parentNodeId={null}
            chartKey={chartKey}
            onReorderGroupChildren={onReorderGroupChildren}
            onOpenNestedExecutiveSummaryGroup={onOpenNestedExecutiveSummaryGroup}
            hierarchyModalReadOnly
            {...sharedProps}
        />
    );
}

function HierarchyTopLevelGrid({
    band: topNodes,
    chartKey,
    onReorderChildren,
    onReorderGroupChildren,
    onOpenNestedExecutiveSummaryGroup,
    ...sharedProps
}) {
    const handleReorderGroupChildren = useCallback(
        (parentNodeId, newChildren) => {
            if (!parentNodeId) {
                onReorderChildren?.(newChildren);
                return;
            }
            onReorderGroupChildren?.(parentNodeId, newChildren);
        },
        [onReorderChildren, onReorderGroupChildren]
    );

    return (
        <NestedGroupGrid
            nodes={topNodes}
            parentNodeId={null}
            chartKey={chartKey}
            onReorderGroupChildren={handleReorderGroupChildren}
            onOpenNestedExecutiveSummaryGroup={onOpenNestedExecutiveSummaryGroup}
            {...sharedProps}
        />
    );
}

const ExecutiveSummaryHierarchyConfigModal = ({
    show = false,
    entity = "project",
    bandName = "band1",
    chartKey = "",
    tile = null,
    onClose,
    onSave,
    onReorderChildren,
    onReorderGroupChildren,
    onPatchDraft,
    entireChartData = {},
    isPreviewDocLoading = false,
    selectedDocPreview,
    handleShowDocPreview,
    updateDocHeaderConfig,
    updatePageBreakConfig,
    toggleImageListForBandModal,
    toggleImageBandDeleteConfirmationModal,
    editImageBand,
    openChartItemConfigModal,
    openExecutiveSummaryHierarchyConfig,
    setSmartChartData,
    currentChartData,
    readOnly = false,
    isNestedModal = false,
    parentIsGroupedBand = false
}) => {
    const [nestedModal, setNestedModal] = useState(null);
    const [showNestedDiscardConfirmation, setShowNestedDiscardConfirmation] = useState(false);
    const [showGroupedConfigEditModal, setShowGroupedConfigEditModal] = useState(false);
    const topBandEntries = useMemo(() => bandChildrenAsArray(tile?.band).map(withInferredHierarchyBandType), [tile?.band]);
    const hasChildren = topBandEntries.length > 0;
    const hideMemoImageEditControls = tile?.is_grouped_band || (isNestedModal && parentIsGroupedBand);
    const currentTileSerialized = useMemo(() => (tile ? JSON.stringify(tile) : ""), [tile]);
    const modalSnapshotKey = useMemo(
        () => `${isNestedModal ? "nested" : "root"}-${entity}-${bandName}-${chartKey}-${show ? "open" : "closed"}`,
        [isNestedModal, entity, bandName, chartKey, show]
    );
    const [initialTileSnapshot, setInitialTileSnapshot] = useState({ key: "", value: "" });
    const hasUnsavedChanges = Boolean(tile) && initialTileSnapshot.key === modalSnapshotKey && initialTileSnapshot.value !== currentTileSerialized;
    const showGroupedConfigSettings = tile?.is_grouped_band === true && !isNestedModal;
    const hierarchyModalGroupedBand = isNestedModal && tile?.is_grouped_band === true;

    useEffect(() => {
        if (!show || !tile) {
            setInitialTileSnapshot({ key: "", value: "" });
            setShowNestedDiscardConfirmation(false);
            setShowGroupedConfigEditModal(false);
            return;
        }
        setInitialTileSnapshot(prev => (prev.key === modalSnapshotKey ? prev : { key: modalSnapshotKey, value: currentTileSerialized }));
    }, [currentTileSerialized, modalSnapshotKey, show, tile]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [topBandEntries, nestedModal]);

    const handleClose = useCallback(() => {
        if (isNestedModal && hasUnsavedChanges) {
            setShowNestedDiscardConfirmation(true);
            return;
        }
        onClose?.();
    }, [hasUnsavedChanges, isNestedModal, onClose]);

    const handleConfirmNestedDiscard = useCallback(() => {
        setShowNestedDiscardConfirmation(false);
        onClose?.();
    }, [onClose]);

    const handleOpenNestedGroup = useCallback(
        groupNode => {
            const normalized = withInferredHierarchyBandType(groupNode);
            const draftRoot = {
                ...normalized,
                exec_sum_parent_wrapper: true,
                name: normalized?.name
            };
            setNestedModal({
                nodeId: hierarchyBandKey(normalized),
                draftRoot: JSON.parse(JSON.stringify(draftRoot)),
                parentIsGroupedBand: tile?.is_grouped_band === true
            });
        },
        [tile?.is_grouped_band]
    );

    const handleNestedClose = useCallback(() => setNestedModal(null), []);

    const handleNestedSave = useCallback(() => {
        if (!nestedModal?.draftRoot || !onPatchDraft || !tile) return;
        onPatchDraft(patchHierarchyNodeById(tile, nestedModal.nodeId, () => nestedModal.draftRoot));
        setNestedModal(null);
    }, [nestedModal, onPatchDraft, tile]);

    const handleNestedReorder = useCallback(newChildren => {
        setNestedModal(prev =>
            prev
                ? {
                      ...prev,
                      draftRoot: { ...prev.draftRoot, band: hierarchyBandEntriesToRecord(newChildren) }
                  }
                : prev
        );
    }, []);

    const handleNestedReorderGroup = useCallback((parentNodeId, newChildren) => {
        setNestedModal(prev => {
            if (!prev?.draftRoot) return prev;
            if (!parentNodeId) {
                return { ...prev, draftRoot: { ...prev.draftRoot, band: hierarchyBandEntriesToRecord(newChildren) } };
            }
            return {
                ...prev,
                draftRoot: patchHierarchyNodeById(prev.draftRoot, parentNodeId, node => ({
                    ...node,
                    band: hierarchyBandEntriesToRecord(newChildren)
                }))
            };
        });
    }, []);

    const handleNestedPatch = useCallback(patchedRoot => {
        setNestedModal(prev => (prev ? { ...prev, draftRoot: patchedRoot } : prev));
    }, []);

    const patchNestedDraftNode = useCallback((params, patchFn) => {
        const targetId = params?.hierarchyNodeId || params?.selectedTile?.innerHierarchyNodeId;
        if (!targetId) return;
        setNestedModal(prev =>
            prev?.draftRoot
                ? {
                      ...prev,
                      draftRoot: patchHierarchyNodeById(prev.draftRoot, targetId, node => patchFn(node))
                  }
                : prev
        );
    }, []);

    const getNestedDraftActionProps = useCallback(
        () => ({
            hierarchyDraftRoot: nestedModal?.draftRoot,
            onHierarchyDraftPatch: handleNestedPatch
        }),
        [handleNestedPatch, nestedModal?.draftRoot]
    );

    const handleNestedDocHeaderConfig = useCallback(
        (params, bodyParam = {}) => {
            patchNestedDraftNode(params, node => ({
                ...node,
                ...(params?.hasOwnProperty?.("include_custom_header") ? { include_custom_header: params?.include_custom_header } : {}),
                ...bodyParam
            }));
        },
        [patchNestedDraftNode]
    );

    const handleNestedPageBreakConfig = useCallback(
        params => {
            patchNestedDraftNode(params, node => ({
                ...node,
                remove_section_break: params?.remove_section_break
            }));
        },
        [patchNestedDraftNode]
    );

    const handleNestedImageListForBandModal = useCallback(
        (params, isView) => {
            toggleImageListForBandModal?.({ ...params, ...getNestedDraftActionProps() }, isView);
        },
        [getNestedDraftActionProps, toggleImageListForBandModal]
    );

    const handleNestedImageBandDeleteConfirmationModal = useCallback(
        params => {
            toggleImageBandDeleteConfirmationModal?.({ ...params, ...getNestedDraftActionProps() });
        },
        [getNestedDraftActionProps, toggleImageBandDeleteConfirmationModal]
    );

    const handleNestedEditImageBand = useCallback(
        (band, tileType) => {
            editImageBand?.({ ...band, ...getNestedDraftActionProps() }, tileType);
        },
        [editImageBand, getNestedDraftActionProps]
    );

    const handleNestedChartItemConfigModal = useCallback(
        params => {
            openChartItemConfigModal?.({ ...params, ...getNestedDraftActionProps() });
        },
        [getNestedDraftActionProps, openChartItemConfigModal]
    );

    const handleGroupedConfigChange = useCallback(
        event => {
            if (!tile || readOnly) return;
            const { name, value } = event.target;
            onPatchDraft?.({
                ...tile,
                [name]: value
            });
        },
        [onPatchDraft, readOnly, tile]
    );

    const handleGroupedConfigEdit = useCallback(
        params => {
            if (!tile || readOnly) return;
            onPatchDraft?.({
                ...tile,
                ...(params?.hasOwnProperty?.("name") ? { group_by_heading: params?.name } : {}),
                ...(params?.hasOwnProperty?.("structured_heading") ? { structured_heading: params?.structured_heading } : {}),
                ...(params?.hasOwnProperty?.("custom_heading_props") && Object.keys(params?.custom_heading_props || {})?.length > 0
                    ? { custom_heading_props: params?.custom_heading_props }
                    : {}),
                ...(params?.hasOwnProperty?.("with_export") ? { with_export: params?.with_export } : {})
            });
            setShowGroupedConfigEditModal(false);
        },
        [onPatchDraft, readOnly, tile]
    );

    const gridProps = {
        band: topBandEntries,
        entity,
        bandName,
        chartKey,
        entireChartData,
        isPreviewDocLoading,
        selectedDocPreview,
        handleShowDocPreview,
        updateDocHeaderConfig,
        updatePageBreakConfig,
        toggleImageListForBandModal,
        toggleImageBandDeleteConfirmationModal,
        editImageBand,
        openChartItemConfigModal,
        openExecutiveSummaryHierarchyConfig,
        onOpenNestedExecutiveSummaryGroup: handleOpenNestedGroup,
        setSmartChartData,
        currentChartData,
        onReorderChildren,
        onReorderGroupChildren,
        hideMemoImageEditControls
    };

    if (!show || !tile) return null;

    return (
        <>
            <Portal
                body={
                    <div
                        className={`modal modal-region modal-view reco-view-mdl column-setting-modal bbi-config-modal executive-summary-tile-selection-modal executive-summary-hierarchy-config-modal chart-drage-area ${isNestedModal ? "" : "first-level-modal"}`}
                        style={{ display: "block" }}
                    >
                        <Draggable handle=".draggable" cancel=".non-draggable">
                            <div className="modal-dialog modal-xl executive-summary-tile-selection-dialog" role="document">
                                <div className="modal-content executive-summary-tile-selection-content">
                                    <div className="modal-header executive-summary-tile-selection-header draggable">
                                        <h5 className="modal-title executive-summary-tile-selection-title">
                                            {readOnly ? "View" : "Configure"}: {tile.name || "Executive summary"}
                                        </h5>
                                        <button type="button" className="close non-draggable" onClick={handleClose}>
                                            <span aria-hidden="true">
                                                <img src="/img/close.svg" alt="" />
                                            </span>
                                        </button>
                                    </div>
                                    <div className="modal-body executive-summary-tile-selection-body non-draggable location-sec">
                                        <div className="executive-summary-tile-selection-table-wrap es-hier-sel-wrap">
                                            <div className="es-hier-sel-card card mb-0">
                                                <div className="es-hier-sel-card-body es-hier-cfg-modal-body card-body pb-0">
                                                    {showGroupedConfigSettings ? (
                                                        <div className="es-hier-cfg-section es-hier-cfg-settings-section card pl-3 pr-3">
                                                            <div className="card-body setting-section p-0 pl-0">
                                                                <h4 className="es-hier-cfg-band-heading mb-1">Configuration Settings</h4>
                                                                <div className="row p-3">
                                                                    <div className="col-md-4 mb-2">
                                                                        <label className="form-label d-flex align-items-center">
                                                                            Group By
                                                                            {groupedExecutiveSummaryConfigValue(tile, "group_by") === "building" ? (
                                                                                <i
                                                                                    className="fas fa-edit ml-2 cursor-hand"
                                                                                    role="button"
                                                                                    title="Edit Configuration"
                                                                                    onClick={() => setShowGroupedConfigEditModal(true)}
                                                                                />
                                                                            ) : null}
                                                                        </label>
                                                                        <select
                                                                            className="form-control"
                                                                            name="group_by"
                                                                            value={groupedExecutiveSummaryConfigValue(tile, "group_by")}
                                                                            onChange={handleGroupedConfigChange}
                                                                            disabled={readOnly}
                                                                        >
                                                                            {GROUPED_EXEC_SUMMARY_CONFIG_OPTIONS.group_by.map(option => (
                                                                                <option key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div className="col-md-4 mb-2">
                                                                        <label className="form-label">Heading Prefix</label>
                                                                        <select
                                                                            className="form-control"
                                                                            name="heading_prefix"
                                                                            value={tile?.heading_prefix || "building_name"}
                                                                            onChange={handleGroupedConfigChange}
                                                                            disabled={readOnly}
                                                                        >
                                                                            {HEADING_PREFIX_OPTIONS.map(option => (
                                                                                <option key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div className="col-md-4 mb-2">
                                                                        <label className="form-label">Sort By</label>
                                                                        <select
                                                                            className="form-control"
                                                                            name="sort_by"
                                                                            value={groupedExecutiveSummaryConfigValue(tile, "sort_by")}
                                                                            onChange={handleGroupedConfigChange}
                                                                            disabled={readOnly}
                                                                        >
                                                                            {GROUPED_EXEC_SUMMARY_CONFIG_OPTIONS.sort_by.map(option => (
                                                                                <option key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div className="col-md-4 mb-2">
                                                                        <label className="form-label">Sort Direction</label>
                                                                        <select
                                                                            className="form-control"
                                                                            name="sort_direction"
                                                                            value={groupedExecutiveSummaryConfigValue(tile, "sort_direction")}
                                                                            onChange={handleGroupedConfigChange}
                                                                            disabled={readOnly}
                                                                        >
                                                                            {GROUPED_EXEC_SUMMARY_CONFIG_OPTIONS.sort_direction.map(option => (
                                                                                <option key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                    <div className="es-hier-cfg-section card mb-0 es-hier-cfg-band-section">
                                                        <div className="card-body ml-3 mr-3">
                                                            {showGroupedConfigSettings ? <h4 className="es-hier-cfg-band-heading">Bands</h4> : null}
                                                            <div className="es-hier-cfg-tile-scroll">
                                                                {!hasChildren ? (
                                                                    <div className="es-hier-cfg-empty p-4 text-center text-muted">
                                                                        No inner bands.
                                                                    </div>
                                                                ) : readOnly ? (
                                                                    <HierarchyTopLevelGridReadOnly {...gridProps} />
                                                                ) : (
                                                                    <HierarchyTopLevelGrid {...gridProps} onReorderChildren={onReorderChildren} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer executive-summary-tile-selection-footer non-draggable">
                                        <button type="button" className="btn btn-secondary btnClr" onClick={handleClose}>
                                            Close
                                        </button>
                                        {!readOnly ? (
                                            <button type="button" className="btn btn-primary" onClick={onSave} disabled={!hasUnsavedChanges}>
                                                Save
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </Draggable>
                    </div>
                }
                onCancel={handleClose}
            />
            {showGroupedConfigEditModal ? (
                <Portal
                    body={
                        <SmartChartDataEditForm
                            onCancel={() => setShowGroupedConfigEditModal(false)}
                            heading="Edit Configuration"
                            selectedData={{ name: tile?.group_by_heading || "" }}
                            selectedTileData={tile}
                            updateData={handleGroupedConfigEdit}
                            buttonText="Update"
                            hasNotes={false}
                            isHeadingStyles
                            isImageTile
                            isReadOnly={readOnly}
                        />
                    }
                    onCancel={() => setShowGroupedConfigEditModal(false)}
                />
            ) : null}
            {nestedModal ? (
                <ExecutiveSummaryHierarchyConfigModal
                    show
                    isNestedModal
                    parentIsGroupedBand={nestedModal.parentIsGroupedBand}
                    readOnly={readOnly}
                    entity={entity}
                    bandName={bandName}
                    chartKey={chartKey}
                    tile={nestedModal.draftRoot}
                    onClose={handleNestedClose}
                    onSave={handleNestedSave}
                    onReorderChildren={handleNestedReorder}
                    onReorderGroupChildren={handleNestedReorderGroup}
                    onPatchDraft={handleNestedPatch}
                    entireChartData={entireChartData}
                    isPreviewDocLoading={isPreviewDocLoading}
                    selectedDocPreview={selectedDocPreview}
                    handleShowDocPreview={handleShowDocPreview}
                    updateDocHeaderConfig={handleNestedDocHeaderConfig}
                    updatePageBreakConfig={handleNestedPageBreakConfig}
                    toggleImageListForBandModal={handleNestedImageListForBandModal}
                    toggleImageBandDeleteConfirmationModal={handleNestedImageBandDeleteConfirmationModal}
                    editImageBand={handleNestedEditImageBand}
                    openChartItemConfigModal={handleNestedChartItemConfigModal}
                    openExecutiveSummaryHierarchyConfig={openExecutiveSummaryHierarchyConfig}
                    setSmartChartData={setSmartChartData}
                    currentChartData={currentChartData}
                />
            ) : null}
            {showNestedDiscardConfirmation ? (
                <Portal
                    body={
                        <ConfirmationModal
                            type="cancel"
                            heading={"Do you want to cancel and lose all changes?"}
                            message={"This action cannot be reverted, are you sure that you need to cancel?"}
                            onNo={() => setShowNestedDiscardConfirmation(false)}
                            onYes={handleConfirmNestedDiscard}
                            onCloseModal={() => setShowNestedDiscardConfirmation(false)}
                        />
                    }
                    onCancel={() => setShowNestedDiscardConfirmation(false)}
                    zIndex={10000}
                />
            ) : null}
        </>
    );
};

export default ExecutiveSummaryHierarchyConfigModal;
